import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import PATH from '@/src/routes/path';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { z } from 'zod';
import { useEffect, useRef, useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { usePrompt } from '@/src/hooks/usePrompt';
import { Form, FormField } from '@/src/components/ui/form';
import { useToast } from '@/src/hooks/useToast';
import SectionCard from '@/src/components/molecule/SectionCard';
import FormContent from '@/src/components/molecule/FormContent';
import RadioGroup from '@/src/components/atom/RadioGroup';
import Input from '@/src/components/atom/Input';
import ImageUpload from '@/src/components/atom/ImageUpload';
import IconButton from '@/src/components/atom/Button/IconButton';
import { Media } from '@/src/models/MeidaDto';
import SectionLabel from '@/src/components/molecule/SectionLabel';
import { urlRegex } from '@/src/utils/regex';
import Select from '@/src/components/atom/Select';
import ImageUploadInfo from '@/components/molecule/ImageUploadInfo';
import useDialog, { DialogType } from '@/src/hooks/useDialog';
import { PutHomeContentRequestBody } from '@/src/apis/rest/homeContent.dtos';
import {
  getHomeContentByIAPI,
  getHomeContentInAppLinkCatetoryAPI,
  putHomeContentAPI,
} from '@/src/apis/rest/homeContent';

const BACK_PATH = PATH.APP_HOME_CONTENT_DETAIL;

const formSchema = () => {
  return z.object({
    contentType: z.string(),
    title: z.string().min(3),
    isExposed: z.boolean(),
    isExposeNewBadge: z.boolean(),
    content: z.string().min(1),
    contentMediaId: z.number(),
  });
};

const DEFAULT_VALUES: Partial<PutHomeContentRequestBody> = {
  contentType: 'IN_APP_PAGE',
  title: '',
  isExposed: true,
  isExposeNewBadge: true,
};

export default function AppHomeContentEditPage() {
  // util
  const { toast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams();

  // query
  const { data: homeContentCategoryOptions } = useQuery({
    queryKey: ['home-content-in-app-link-category-options'],
    queryFn: getHomeContentInAppLinkCatetoryAPI,
    select: data =>
      (data?.rows || []).map(item => ({ label: item.key, value: item.value })),
  });
  const { data: homeContentData } = useQuery({
    queryKey: ['home-content', id],
    queryFn: () => getHomeContentByIAPI({ id: Number(id) }),
    meta: {
      backUrl: BACK_PATH,
      callback: () => {
        navigate(BACK_PATH, { replace: true });
      },
    },
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    refetchOnReconnect: false,
  });

  // form
  const form = useForm<
    PutHomeContentRequestBody & {
      contentMedia?: Media;
    }
  >({
    resolver: zodResolver(formSchema()),
    defaultValues: DEFAULT_VALUES,
  });
  const { field: mediaField } = useController({
    name: 'contentMedia',
    control: form.control,
  });
  const contentController = useController({
    name: 'content',
    control: form.control,
  });

  // state
  const [defaultData, setDefaultData] = useState('');
  const [isPorceReplace] = useState(false);
  const {
    Dialog: EditDialog,
    isOpen: isEditOpen,
    openDialog: openEditDialog,
    closeDialog: closeEditDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });
  const formRef = useRef<HTMLFormElement>(null);
  const [isExUrlError, setIsExUrlError] = useState(false);

  // mutation
  const putHomeContentMutation = useMutation({ mutationFn: putHomeContentAPI });

  // event
  const handleGoBack = () => {
    navigate(-1);
  };

  const getIsContentValid = () => {
    switch (form.watch().contentType) {
      case 'EXTERNAL_LINK':
        return (
          !!form.watch().content && urlRegex.test(form.watch().content || '')
        );
      default:
        return true;
    }
  };

  const handleSubmit = (values: any) => {
    const newValues = {
      ...values,
    };

    delete newValues.media;
    delete newValues.contentId;

    putHomeContentMutation.mutate(
      {
        ...newValues,
        id: Number(id),
      },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: '성공',
            description: '성공적으로 저장되었습니다.',
          });

          navigate(BACK_PATH.replace(':id', String(id)));
        },
        onError: e => {
          toast({
            variant: 'error',
            title: '실패',
            description: e.message,
          });
        },
      },
    );
  };

  const handleResetContent = () => {
    contentController.field.onChange('');
  };

  // render
  const renderConnectContent = () => {
    switch (form.watch('contentType')) {
      case 'IN_APP_PAGE':
        return (
          <FormField
            control={form.control}
            name="content"
            rules={{
              required: true,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <FormContent isRequired label="연결 컨텐츠" error={error}>
                  <Select
                    rootProps={{
                      value: field?.value || undefined,
                      onValueChange: field.onChange,
                    }}
                    valueProps={{
                      placeholder: '연결할 앱 내 화면을 선택해주세요',
                    }}
                    option={[
                      {
                        optionList: homeContentCategoryOptions || [],
                      },
                    ]}
                  />
                </FormContent>
              );
            }}
          />
        );
      case 'EXTERNAL_LINK':
        return (
          <FormField
            control={form.control}
            name="content"
            render={({ field }) => {
              return (
                <FormContent
                  isRequired
                  label="연결 컨텐츠"
                  error={
                    isExUrlError
                      ? {
                          type: 'pattern',
                          message:
                            'url 형식을 확인해주세요 (https://example.com)',
                        }
                      : undefined
                  }
                >
                  <Input
                    value={field?.value || undefined}
                    onChange={e => {
                      field.onChange(e.target.value);

                      if (e.target.value) {
                        setIsExUrlError(!urlRegex.test(e.target.value));
                      } else {
                        setIsExUrlError(false);
                      }
                    }}
                    onClear={() => {
                      field.onChange('');
                      setIsExUrlError(false);
                    }}
                    containerClassName="flex-1"
                    placeholder="링크를 입력해주세요 (https://example.com)"
                  />
                </FormContent>
              );
            }}
          />
        );
      default:
        return (
          <FormField
            control={form.control}
            name="content"
            rules={{
              required: true,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <FormContent isRequired label="연결 컨텐츠" error={error}>
                  <Select
                    rootProps={{
                      value: field?.value || undefined,
                      onValueChange: field.onChange,
                    }}
                    valueProps={{
                      placeholder: '연결할 앱 내 화면을 선택해주세요',
                    }}
                    option={[
                      {
                        optionList: homeContentCategoryOptions || [],
                      },
                    ]}
                  />
                </FormContent>
              );
            }}
          />
        );
    }
  };

  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={handleGoBack}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              이전 화면
            </div>
          </Button>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={openEditDialog}
              disabled={
                !(defaultData !== JSON.stringify(form.watch())) ||
                !form.formState.isValid ||
                !getIsContentValid()
              }
              className="w-40"
              buttonSize="medium"
              isLoading={putHomeContentMutation.isPending}
            >
              저장
            </Button>
          </div>
        </div>
      </BottomFixedContainer>
    );
  };

  // effect
  useEffect(() => {
    if (homeContentData) {
      const defaultFormData = {
        ...homeContentData,
        contentMediaId: homeContentData?.contentMedia?.id,
      };

      form.reset(defaultFormData);

      setDefaultData(JSON.stringify(defaultFormData));
    }
  }, [homeContentData, form]);

  // usePrompt
  usePrompt(
    defaultData !== JSON.stringify(form.watch()) &&
      !form.formState.isSubmitted &&
      !isPorceReplace,
  );

  return (
    <>
      <MainContentTitle
        breadcrumbList={['홈 퀵메뉴 관리', '홈 퀵메뉴 수정']}
        pageTitle="홈 퀵메뉴 수정"
      />
      <Form {...form}>
        <form
          ref={formRef}
          className="relative mb-[100px]"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <SectionCard cardTitle="홈 퀵메뉴 정보">
            <div className="grid grid-cols-[1fr_auto_1fr] gap-x-6 gap-y-4">
              <div className="flex flex-col gap-6">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent label="제목" isRequired error={error}>
                        <Input
                          {...field}
                          containerClassName="flex-1"
                          placeholder="제목을 입력해주세요 (3~5자로 설정 가능합니다)"
                          onClear={() => {
                            field.onChange('');
                          }}
                          maxLength={5}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="isExposed"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent label="노출 여부" isRequired error={error}>
                        <RadioGroup
                          optionList={[
                            { label: '노출', value: 'true' },
                            { label: '미노출', value: 'false' },
                          ]}
                          rootProps={{
                            value: field.value ? 'true' : 'false',
                            onValueChange: (e: string) =>
                              field.onChange(e === 'true'),
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="contentMediaId"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label="아이콘 이미지"
                        isRequired
                        error={error}
                        defaultMessage={
                          <ImageUploadInfo widthSize={44} heightSize={44} />
                        }
                      >
                        <ImageUpload
                          width={140}
                          height={140}
                          widthText={44}
                          heightText={44}
                          maxImageSizeMb={5}
                          imageClassName="object-cover"
                          value={mediaField.value?.bufferFullUrl}
                          onChange={e => {
                            field.onChange(e.id);
                            mediaField.onChange(e);
                          }}
                          topAccessary={({ handleEdit, handleRemove }) => {
                            return (
                              <>
                                <div className="flex gap-x-1">
                                  <IconButton
                                    buttonSize="xSmall"
                                    buttonColor="outline"
                                    lucideIconProps={{ name: 'Pencil' }}
                                    onClick={() => {
                                      handleEdit();
                                    }}
                                  />
                                  <IconButton
                                    buttonSize="xSmall"
                                    buttonColor="outline"
                                    lucideIconProps={{ name: 'X' }}
                                    onClick={() => {
                                      handleRemove();
                                      field.onChange(undefined);
                                    }}
                                  />
                                </div>
                              </>
                            );
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
              </div>
              <div className="border-r border-r-gray1" />
              <div className="flex flex-col gap-6">
                <FormField
                  control={form.control}
                  name="isExposeNewBadge"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label="신규 뱃지 알림 여부"
                        isRequired
                        error={error}
                      >
                        <RadioGroup
                          optionList={[
                            {
                              label: '알림 노출',
                              value: 'true',
                            },
                            {
                              label: '알림 미노출',
                              value: 'false',
                            },
                          ]}
                          rootProps={{
                            value: field.value ? 'true' : 'false',
                            onValueChange: (e: string) =>
                              field.onChange(e === 'true'),
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="contentType"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label="컨텐츠 유형 선택"
                        isRequired
                        error={error}
                      >
                        <RadioGroup
                          optionList={[
                            {
                              label: '앱 내 화면',
                              value: 'IN_APP_PAGE',
                            },
                            {
                              label: '외부 링크 연결',
                              value: 'EXTERNAL_LINK',
                            },
                          ]}
                          rootProps={{
                            value: field.value,
                            onValueChange: e => {
                              field.onChange(e);
                              setIsExUrlError(false);
                              handleResetContent();
                            },
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                {form.watch()?.contentType ? (
                  renderConnectContent()
                ) : (
                  <div className="h-[40px]" />
                )}
                <SectionLabel
                  labelProps={{
                    className: 'translate-y-2',
                  }}
                  label="등록 일시"
                  value={
                    <Input
                      containerClassName="flex-1"
                      value={dayjs(homeContentData?.createdAt).format(
                        'YYYY-MM-DD HH:mm:ss',
                      )}
                      disabled
                    />
                  }
                />
                <SectionLabel
                  labelProps={{
                    className: 'translate-y-2',
                  }}
                  label="등록 관리자"
                  value={
                    <Input
                      containerClassName="flex-1"
                      value={homeContentData?.administrator?.name}
                      disabled
                    />
                  }
                />
              </div>
            </div>
          </SectionCard>
          {renderBottomFixedContent()}
        </form>
      </Form>

      {/* 모달 */}
      {isEditOpen && (
        <EditDialog
          open={isEditOpen}
          onOk={() => formRef?.current?.requestSubmit()}
          onOpenChange={e => {
            if (e) {
              openEditDialog();
            } else {
              closeEditDialog();
            }
          }}
          title="수정된 내용을 저장하시겠어요?"
          isLoading={putHomeContentMutation.isPending}
        />
      )}
    </>
  );
}
